import {
    type Dispatch,
    type SetStateAction,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import type { RowSelectionState } from "@tanstack/react-table";
import { pluralizeWithCount } from "@common/helpers/string";
import { useCurrentOrganizationMembers } from "@api/organizationMember.api";
import type { WorkItemEdit } from "@api/workItem.api";
import { CommandsDirectory } from "@components/commands/lib";
import { useCommands } from "@components/commands/useCommands";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { useStateAwareNavigate } from "@hooks/useStateAwareNavigate";
import { useScope } from "@state/scopeContext.lib";
import type {
    ClientsWorkOverviewWorkRow,
    ClientsWorkOverviewWorkspaceRow,
} from "../clientsWorkOverviewViewConfig";
import { useConfirmWorkStartChange } from "../editing/useConfirmWorkStartChange";
import { useWorkEditability } from "../editing/workItemEditing";
import { useWorkBulkDelete } from "../useWorkBulkDelete";
import type { SelectedWorkEngagement } from "../useWorkRowSelection";
import {
    getWorkOpenClientLink,
    getWorkViewInActionsLink,
} from "../workItemLinks";
import { useWorkBulkEdits } from "./useWorkBulkEdits";
import { getWorkDateSteps, getWorkMemberOptions } from "./workCommandOptions";

interface UseWorkCommandsArgs {
    workspaceRows: ClientsWorkOverviewWorkspaceRow[] | null;
    selectedEngagements: SelectedWorkEngagement[];
    setRowSelection: Dispatch<SetStateAction<RowSelectionState>>;
    activeRow: ClientsWorkOverviewWorkRow | null;
}

/**
 * The Work page's Cmd+K: it acts on the checked items, or on the open one
 * when nothing is checked. Returns the opener (also bound to mod+k while
 * there is something to act on) and the Cancel and Delete confirmations.
 */
export const useWorkCommands = ({
    workspaceRows,
    selectedEngagements,
    setRowSelection,
    activeRow,
}: UseWorkCommandsArgs) => {
    const { open, close, registerOpenOverride, unregisterOpenOverride } =
        useCommands();
    const navigate = useStateAwareNavigate();
    const { data: members = [] } = useCurrentOrganizationMembers();
    const { organizationId = null } = useScope();
    const { editAll, cancelAll } = useWorkBulkEdits();
    const { saveEdit, confirmationModal: workStartConfirmation } =
        useConfirmWorkStartChange();
    const { isItemEditable, isRoleEditable } = useWorkEditability();
    const [confirmation, setConfirmation] = useState<
        "cancel" | "delete" | null
    >(null);

    const isSelection = selectedEngagements.length > 0;
    const engagements = useMemo(() => {
        if (isSelection || !activeRow) {
            return selectedEngagements;
        }
        return [{ closeId: activeRow.closeId, rowIds: [activeRow.id] }];
    }, [isSelection, selectedEngagements, activeRow]);
    const rows = useMemo(() => {
        const rowsByCloseId = new Map<string, ClientsWorkOverviewWorkRow>();
        for (const row of workspaceRows?.flatMap((ws) => ws.children) ?? []) {
            rowsByCloseId.set(row.closeId, row);
        }
        return engagements.flatMap(({ closeId }) => {
            const row = rowsByCloseId.get(closeId);
            return row ? [row] : [];
        });
    }, [workspaceRows, engagements]);
    const editableRows = useMemo(
        () => rows.filter(isItemEditable),
        [rows, isItemEditable],
    );
    const deleteAll = useWorkBulkDelete({
        selectedEngagements: engagements,
        setRowSelection,
    });

    const openCommands = useCallback(() => {
        const onEdit = (edit: WorkItemEdit) =>
            saveEdit({
                edit,
                itemCount: editableRows.length,
                save: () => void editAll(rows, edit),
            });
        const confirm = (kind: "cancel" | "delete") => () => {
            close();
            setConfirmation(kind);
        };
        const goTo = (link: string | null | undefined) =>
            link
                ? () => {
                      close();
                      navigate(link);
                  }
                : undefined;
        const hasEdits = editableRows.length > 0;
        const singleRow = isSelection ? null : activeRow;
        open(CommandsDirectory.CLIENTS_WORK_BULK, {
            scopeLabel: singleRow
                ? singleRow.entityName || singleRow.workspaceName
                : undefined,
            includeGlobalCommands: singleRow !== null,
            clientsWork: {
                heading: singleRow
                    ? `${singleRow.workspaceName} · ${singleRow.name}`
                    : `${pluralizeWithCount("item", engagements.length)} selected`,
                memberOptions: hasEdits
                    ? getWorkMemberOptions({
                          rows: editableRows,
                          members,
                          organizationId,
                          isRoleEditable,
                          onEdit,
                      })
                    : undefined,
                dateSteps: hasEdits
                    ? getWorkDateSteps({ rows: editableRows, onEdit })
                    : undefined,
                onCancel: hasEdits ? confirm("cancel") : undefined,
                onViewInActions: goTo(
                    singleRow && getWorkViewInActionsLink(singleRow),
                ),
                onOpenClient: goTo(
                    singleRow && getWorkOpenClientLink(singleRow),
                ),
                onDelete: confirm("delete"),
            },
        });
    }, [
        rows,
        editableRows,
        isRoleEditable,
        saveEdit,
        editAll,
        close,
        open,
        isSelection,
        engagements.length,
        activeRow,
        members,
        organizationId,
        navigate,
    ]);

    const hasTargets = engagements.length > 0;
    const openCommandsRef = useRef(openCommands);
    openCommandsRef.current = openCommands;
    useEffect(() => {
        if (hasTargets) {
            registerOpenOverride(() => openCommandsRef.current());
            return () => unregisterOpenOverride();
        }
    }, [hasTargets, registerOpenOverride, unregisterOpenOverride]);

    const closeConfirmation = () => setConfirmation(null);
    const deleteCount = engagements.length;
    const modals = (
        <>
            {workStartConfirmation}
            {confirmation === "delete" && (
                <ConfirmationModal
                    show
                    title={`Delete ${pluralizeWithCount("work item", deleteCount)}?`}
                    question={`This will permanently delete ${pluralizeWithCount("work item", deleteCount)} and all associated tasks. This action can't be undone.`}
                    yes="Delete"
                    yesBtnVariant="destructive"
                    onConfirm={async () => {
                        await deleteAll();
                        closeConfirmation();
                    }}
                    onReject={closeConfirmation}
                    onHide={closeConfirmation}
                />
            )}
            {confirmation === "cancel" && (
                <ConfirmationModal
                    show
                    title={`Cancel ${pluralizeWithCount("work item", editableRows.length)}?`}
                    question={
                        rows.length > editableRows.length
                            ? "This can't be undone. Done and canceled items are skipped."
                            : "This can't be undone."
                    }
                    yes="Yes, cancel"
                    onConfirm={async () => {
                        closeConfirmation();
                        await cancelAll(rows);
                    }}
                    onReject={closeConfirmation}
                    onHide={closeConfirmation}
                />
            )}
        </>
    );

    return { openCommands, modals };
};
